import { useState } from "react"
import { faClose, faGear, faListOl } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import type { RodizioSettings } from "../../../Objects/Rodizio/RodizioSettings"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import GetSpace from "../../../utils/space"

interface Props {
  settings: RodizioSettings
  isLoading: boolean
  onClose: () => void
  onSave: (settings: RodizioSettings) => Promise<boolean>
}

export const RodizioSettingsPopup: React.FC<Props> = ({ settings, isLoading, onClose, onSave }) => {
  const { t } = useTranslation("rodizio")

  const [draft, setDraft] = useState({ ...settings })

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("settings.title")}
              icon={faGear}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex-nowrap`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="rodizio.settings"
                    title={t("helpPopup:global.rodizioSettings.title")}
                    description={t("helpPopup:global.rodizioSettings.description")}
                    steps={t("helpPopup:global.rodizioSettings.steps", { returnObjects: true }) as string[]}
                    bullets={t("helpPopup:global.rodizioSettings.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace left={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    descriptionKey="interactionDescriptions:rodizio.settings.close"
                  />
                </div>
              }
            />

            <form
              className="grid gap-3 p-2"
              onSubmit={async (event) => {
                event.preventDefault()
                if (await onSave(draft)) onClose()
              }}
            >
              <SimpleToggle
                title={t("settings.enabled")}
                value={draft.isEnabled}
                onChange={(value) => setDraft((current) => ({ ...current, isEnabled: value }))}
                descriptionKey="interactionDescriptions:rodizio.settings.enabled"
              />

              <SimpleToggle
                title={t("settings.waiter")}
                value={draft.allowWaiterOrders}
                disabled={!draft.isEnabled}
                onChange={(value) => setDraft((current) => ({ ...current, allowWaiterOrders: value }))}
                descriptionKey="interactionDescriptions:rodizio.settings.waiter"
              />

              <SimpleToggle
                title={t("settings.tablet")}
                value={draft.allowTableTabletOrders}
                disabled={!draft.isEnabled}
                onChange={(value) => setDraft((current) => ({ ...current, allowTableTabletOrders: value }))}
                descriptionKey="interactionDescriptions:rodizio.settings.tablet"
              />

              <SimpleToggle
                title={t("settings.customer")}
                value={draft.allowCustomerMobileOrders}
                disabled={!draft.isEnabled}
                onChange={(value) => setDraft((current) => ({ ...current, allowCustomerMobileOrders: value }))}
                descriptionKey="interactionDescriptions:rodizio.settings.customer"
              />

              <InputField
                type="number"
                icon={faListOl}
                value={String(draft.maximumItemsPerRequest)}
                min={1}
                max={100}
                required
                placeholder={t("settings.maximum_items")}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, maximumItemsPerRequest: Number(event.target.value) }))
                }
                descriptionKey="interactionDescriptions:rodizio.settings.maximumItems"
              />

              {draft.isEnabled &&
                !draft.allowWaiterOrders &&
                !draft.allowTableTabletOrders &&
                !draft.allowCustomerMobileOrders && (
                  <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm font-bold text-amber-800">
                    {t("settings.channel_required")}
                  </p>
                )}

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                <Button
                  label={isLoading ? t("common.saving") : t("common.save")}
                  typeButton="submit"
                  disabled={
                    isLoading ||
                    draft.maximumItemsPerRequest < 1 ||
                    draft.maximumItemsPerRequest > 100 ||
                    (draft.isEnabled &&
                      !draft.allowWaiterOrders &&
                      !draft.allowTableTabletOrders &&
                      !draft.allowCustomerMobileOrders)
                  }
                  bgColor={THEME.colors.buttonBGSaveColor}
                  descriptionKey="interactionDescriptions:rodizio.settings.save"
                />
              </div>
            </form>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
